import { Editor } from "@/core/editor"
import { vec2 } from "gl-matrix"
import { PixelData } from "@/core/utils/image-data"
import { fillToSize, fitToSize } from "@/core/utils/resize-image"
import { encodeTIFF, type TiffEncoderLike } from "@/core/encoders/tiff-encoder"
import { encodeDNG } from "@/core/encoders/dng-encoder"
import { encodePNG } from "@/core/encoders/png-encoder"
import { PhotoMetadataRecord } from "@/lib/catalog"
import { Adjustments } from "@/core/types"
import { getDefaultAdjustments } from "@/core/adjustments"
import { atomWithStorage } from "jotai/utils"
import { safeJSONLocalStorage } from "@/utils/safe-jotai-storage"
import { encodePSD, type PSDEncoderLike } from "./encoders/psd-encoder"
import { Sentry } from "@/telemetry/sentry"
import { encodeJpeg, type JpegEncoderLike } from "./encoders/jpeg-encoder"
import { focusAtom } from "jotai-optics"
import type { RGBPlaneOutput } from "./pipeline/passes/rgb-plane-output-pass"
import type { YUVOutput } from "./pipeline/passes/yuv-output-pass"
import type { TextureFormat } from "./backends/webgl/texture-format"
import type { RendererOutput } from "./pipeline/renderer"
export type ExportImageType = "JPEG" | "TIFF" | "PSD"
export type DimensionType = "Full size" | "Custom"
export type DimensionSide = "Short side" | "Long side"
export type SelectedOption = "selected" | "all"
export type BitDepth = 16 | 8

export interface PublishOptions {
	imageType: ExportImageType
	dimensionType: DimensionType
	dimensionSide: DimensionSide
	dimension: number
	bitDepth: BitDepth
	quality: number
	colorSpace: PredefinedColorSpace
	pixelPerInch: number
	watermarkId: number | null

	selectedOption: SelectedOption
}

export interface ExportOptions {
	imageType: ExportImageType
	dimensionType: DimensionType
	dimensionSide: DimensionSide
	dimension: number
	bitDepth: BitDepth
	quality: number
	colorSpace: PredefinedColorSpace
	pixelPerInch: number

	selectedOption: SelectedOption
	filenameType: FilenameType
	filenamePrefix: string
	filenameNumber: number
}

export const defaultExportOptions: ExportOptions = {
	imageType: "JPEG",
	dimensionType: "Full size",
	dimensionSide: "Long side",
	dimension: 1024,
	selectedOption: "selected",
	bitDepth: 16,
	quality: 0.9,
	colorSpace: "srgb",
	pixelPerInch: 300,
	filenameType: "original",
	filenamePrefix: "Untitled_",
	filenameNumber: 1,
}

export const defaultPublishOptions: PublishOptions = {
	imageType: "JPEG",
	dimensionType: "Full size",
	dimensionSide: "Long side",
	dimension: 1024,
	bitDepth: 16,
	quality: 0.9,
	colorSpace: "srgb",
	pixelPerInch: 300,
	watermarkId: null,
	selectedOption: "selected",
}

export const exportOptionsAtom = atomWithStorage(
	"exportOptions",
	defaultExportOptions,
	safeJSONLocalStorage<ExportOptions>(),
)
export const publishOptionsAtom = atomWithStorage(
	"publishOptions",
	defaultPublishOptions,
	safeJSONLocalStorage<PublishOptions>(),
)
const hasCompletePublishOptions = (options: PublishOptions): boolean => {
	return Object.keys(defaultPublishOptions).every(
		(key) => Reflect.get(options, key) !== undefined,
	)
}

publishOptionsAtom.onMount = (setPublishOptions) => {
	setPublishOptions((prev) => {
		if (hasCompletePublishOptions(prev)) {
			return prev
		}
		return { ...defaultPublishOptions, ...prev }
	})
}

export const publishImageTypeAtom = focusAtom(publishOptionsAtom, (optic) =>
	optic.prop("imageType"),
)
export const publishDimensionTypeAtom = focusAtom(publishOptionsAtom, (optic) =>
	optic.prop("dimensionType"),
)
export const publishDimensionSideAtom = focusAtom(publishOptionsAtom, (optic) =>
	optic.prop("dimensionSide"),
)
export const publishDimensionAtom = focusAtom(publishOptionsAtom, (optic) =>
	optic.prop("dimension"),
)
export const publishBitDepthAtom = focusAtom(publishOptionsAtom, (optic) => optic.prop("bitDepth"))
export const publishQualityAtom = focusAtom(publishOptionsAtom, (optic) => optic.prop("quality"))
export const publishColorSpaceAtom = focusAtom(publishOptionsAtom, (optic) =>
	optic.prop("colorSpace"),
)
export const publishPixelPerInchAtom = focusAtom(publishOptionsAtom, (optic) =>
	optic.prop("pixelPerInch"),
)
export const publishWatermarkAtom = focusAtom(publishOptionsAtom, (optic) =>
	optic.prop("watermarkId"),
)
export const publishSelectedOptionAtom = focusAtom(publishOptionsAtom, (optic) =>
	optic.prop("selectedOption"),
)

export const exportImageTypeAtom = focusAtom(exportOptionsAtom, (optic) => optic.prop("imageType"))
export const exportDimensionTypeAtom = focusAtom(exportOptionsAtom, (optic) =>
	optic.prop("dimensionType"),
)
export const exportDimensionSideAtom = focusAtom(exportOptionsAtom, (optic) =>
	optic.prop("dimensionSide"),
)
export const exportDimensionAtom = focusAtom(exportOptionsAtom, (optic) => optic.prop("dimension"))
export const exportBitDepthAtom = focusAtom(exportOptionsAtom, (optic) => optic.prop("bitDepth"))
export const exportQualityAtom = focusAtom(exportOptionsAtom, (optic) => optic.prop("quality"))
export const exportColorSpaceAtom = focusAtom(exportOptionsAtom, (optic) =>
	optic.prop("colorSpace"),
)
export const exportPixelPerInchAtom = focusAtom(exportOptionsAtom, (optic) =>
	optic.prop("pixelPerInch"),
)

export const selectedOptionAtom = focusAtom(exportOptionsAtom, (optic) =>
	optic.prop("selectedOption"),
)
export const filenameTypeAtom = focusAtom(exportOptionsAtom, (optic) => optic.prop("filenameType"))
export const filenamePrefixAtom = focusAtom(exportOptionsAtom, (optic) =>
	optic.prop("filenamePrefix"),
)
export const filenameNumberAtom = focusAtom(exportOptionsAtom, (optic) =>
	optic.prop("filenameNumber"),
)

// NOTE: DNG is not fully supported yet
export type ImageType = "jpg" | "tif" | "png" | "dng" | "psd"
export type FilenameType = "original" | "date" | "custom-sequence" | "custom-originalNumber"
export type ExportImageDataFormat = "yuv" | "rgba" | "rgb-planar"

interface ResizeOptions {
	resizeWidth?: number
	resizeHeight?: number
	resizeMode?: "fit" | "fill"
}

export const MAX_CUSTOM_EXPORT_DIMENSION = 12_000

export function isExportImageSizeSupported([width, height]: vec2): boolean {
	return width <= MAX_CUSTOM_EXPORT_DIMENSION && height <= MAX_CUSTOM_EXPORT_DIMENSION
}

export interface ExportImageDataOptions extends ResizeOptions {
	format: ExportImageDataFormat
	bitDepth?: 8 | 16 // 16-bit is only supported for TIFF and DNG
	colorSpace?: PredefinedColorSpace
	ppi?: number
	adjustments?: Partial<Adjustments>
	bufferView?: Uint8Array | Uint16Array
}

export interface ExportImageOptions extends ExportImageDataOptions {
	type: ImageType
	filenameType: FilenameType
	quality?: number // JPEG quality 0-1
	filenamePrefix?: string
	filenameNumber?: number
	metadata?: PhotoMetadataRecord
	jpegEncoder?: JpegEncoderLike
	tiffEncoder?: TiffEncoderLike
	psdEncoder?: PSDEncoderLike
	projectId?: string
	watermark?: number | null
}

interface EncodeImageDataOptionsBase {
	quality?: number
	ppi?: number
	metadata?: PhotoMetadataRecord
	xmpData?: Record<string, any>
	tiffEncoder?: TiffEncoderLike
	psdEncoder?: PSDEncoderLike
}

export type JpegEncodeImageDataOptions = EncodeImageDataOptionsBase & {
	type: "jpg"
	jpegEncoder: JpegEncoderLike
}

export type NonJpegEncodeImageDataOptions = EncodeImageDataOptionsBase & {
	type: Exclude<ImageType, "jpg">
	jpegEncoder?: JpegEncoderLike
}

type AnyImageTypeEncodeImageDataOptions = EncodeImageDataOptionsBase & {
	type: ImageType
	jpegEncoder: JpegEncoderLike
}

export type EncodeImageDataOptions = JpegEncodeImageDataOptions | NonJpegEncodeImageDataOptions

type JpegExportImageOptions = ExportImageOptions & {
	type: "jpg"
	jpegEncoder: JpegEncoderLike
}

type NonJpegExportImageOptions = ExportImageOptions & {
	type: Exclude<ImageType, "jpg">
}

type AnyImageTypeExportImageOptions = ExportImageOptions & {
	type: ImageType
	jpegEncoder: JpegEncoderLike
}

export function getExportImageSize(sourceSize: vec2, options: ResizeOptions): vec2 {
	return resizeDimensions(sourceSize, options)
}

export function getYUVExportByteLength(width: number, height: number): number {
	const [pixelWidth, pixelHeight] = normalizeExportSize([width, height])
	const yByteLength = pixelWidth * pixelHeight
	const uvByteLength = Math.ceil(pixelWidth / 2) * Math.ceil(pixelHeight / 2)

	return yByteLength + uvByteLength * 2
}

export async function exportImageData(
	editor: Editor,
	options: ExportImageDataOptions,
): Promise<PixelData> {
	// Copy photo so that we don't overwrite the currently loaded photo adjustments
	// TODO is it format related?
	const defaultAdjustments = getDefaultAdjustments(editor.photo)
	const photo = options.adjustments
		? editor.photo.clone({ adjustments: { ...defaultAdjustments, ...options.adjustments } })
		: editor.photo

	const [width, height] = resizeDimensions(photo.imageState.croppedImageSize, options)
	const hasCustomDimensions =
		options.resizeWidth !== undefined || options.resizeHeight !== undefined
	if (hasCustomDimensions && !isExportImageSizeSupported([width, height])) {
		throw new Error(
			`Custom export dimensions cannot exceed ${MAX_CUSTOM_EXPORT_DIMENSION} × ${MAX_CUSTOM_EXPORT_DIMENSION} pixels`,
		)
	}

	// Make sure all assets are loaded (segmentation masks, etc...)
	await editor.renderer.assets.resolveAll(photo)

	const bitDepth = options.bitDepth === 16 ? 16 : 8
	const colorSpace = options.colorSpace ?? "display-p3"

	const format: TextureFormat =
		bitDepth === 16 && editor.backend.unorm16supported ? "rgba16unorm" : "rgba8unorm"
	const rgbPlaneFormat: TextureFormat =
		bitDepth === 16 && editor.backend.unorm16supported ? "r16unorm" : "r8unorm"
	const outputTexture = editor.renderer.scratchTexture

	let yuvOutput: YUVOutput | null = null
	let rgbPlaneOutput: RGBPlaneOutput | null = null
	let renderOutput: RendererOutput

	if (options.format === "yuv") {
		yuvOutput = {
			y: outputTexture,
			u: editor.renderer.scratchBuffers.readBuffer,
			v: editor.renderer.scratchBuffers.writeBuffer,
		}

		// Y texture
		yuvOutput.y.update({
			width,
			height,
			format: "r8unorm",
		})

		// U texture
		yuvOutput.u.update({
			width: (width + 1) >> 1,
			height: (height + 1) >> 1,
			format: "r8unorm",
		})

		// V texture
		yuvOutput.v.update({
			width: (width + 1) >> 1,
			height: (height + 1) >> 1,
			format: "r8unorm",
		})
		renderOutput = {
			format: "yuv",
			...yuvOutput,
		}
	} else if (options.format === "rgb-planar") {
		rgbPlaneOutput = {
			r: outputTexture,
			g: editor.renderer.scratchBuffers.readBuffer,
			b: editor.renderer.scratchBuffers.writeBuffer,
		}

		rgbPlaneOutput.r.update({
			width,
			height,
			format: rgbPlaneFormat,
		})

		rgbPlaneOutput.g.update({
			width,
			height,
			format: rgbPlaneFormat,
		})

		rgbPlaneOutput.b.update({
			width,
			height,
			format: rgbPlaneFormat,
		})
		renderOutput = {
			format: "rgb-planar",
			...rgbPlaneOutput,
		}
	} else {
		// RGBA unified texture
		outputTexture.update({
			width,
			height,
			format,
		})
		renderOutput = {
			format: "rgba",
			texture: outputTexture,
		}
	}

	const wasHistogramEnabled = editor.renderer.histogram.isEnabled
	editor.renderer.histogram.setEnabled(false)

	try {
		editor.renderer.render(photo, {
			width,
			height,
			colorSpace,
			output: renderOutput,
		})
	} catch (e) {
		// We only hit certain errors when editor was cleaned up already
		console.warn("Couldn't render photo because editor was cleaned up")
		Sentry.captureMessage("Couldn't render photo for export because editor was cleaned up")
	}

	editor.renderer.histogram.setEnabled(wasHistogramEnabled)

	// Reset viewport texture so we know to update it
	editor.mainViewport.setTexture(null)

	if (options.format === "yuv" && yuvOutput) {
		const yByteLength = yuvOutput.y.getByteLength()
		const uByteLength = yuvOutput.u.getByteLength()
		const vByteLength = yuvOutput.v.getByteLength()
		const byteLength = yByteLength + uByteLength + vByteLength

		if (options.bufferView && !(options.bufferView instanceof Uint8Array)) {
			throw new Error("YUV export buffer must be a Uint8Array")
		}

		if (options.bufferView && options.bufferView.byteLength < byteLength) {
			throw new Error("YUV export buffer is smaller than the required byte length")
		}

		let data = options.bufferView
		if (!data) {
			data = new Uint8Array(new ArrayBuffer(byteLength))
		}

		const yView = data.subarray(0, yByteLength)
		const uView = data.subarray(yByteLength, yByteLength + uByteLength)
		const vView = data.subarray(yByteLength + uByteLength, byteLength)

		await Promise.all([
			yuvOutput.y.readAsync({ packAlignment: 1, bufferView: yView }),
			yuvOutput.u.readAsync({ packAlignment: 1, bufferView: uView }),
			yuvOutput.v.readAsync({ packAlignment: 1, bufferView: vView }),
		])

		editor.renderer.scratchBuffers.updateSize(0, 0)

		return {
			data,
			yuvDataLayout: {
				yOffset: 0,
				yLength: yByteLength,

				uOffset: yByteLength,
				uLength: uByteLength,

				vOffset: yByteLength + uByteLength,
				vLength: vByteLength,
			},
			width,
			height,
			colorSpace,
		}
	} else if (rgbPlaneOutput) {
		const rByteLength = rgbPlaneOutput.r.getByteLength()
		const gByteLength = rgbPlaneOutput.g.getByteLength()
		const bByteLength = rgbPlaneOutput.b.getByteLength()
		const byteLength = rByteLength + gByteLength + bByteLength
		const bytesPerSample = rByteLength / (width * height)

		if (options.bufferView && options.bufferView.byteLength < byteLength) {
			throw new Error("RGB planes export buffer is smaller than the required byte length")
		}

		const hasMismatchedBufferType =
			options.bufferView &&
			((bytesPerSample === 2 && !(options.bufferView instanceof Uint16Array)) ||
				(bytesPerSample === 1 && !(options.bufferView instanceof Uint8Array)))

		if (hasMismatchedBufferType) {
			throw new Error(
				"RGB planes export buffer type does not match the output texture format",
			)
		}

		let data = options.bufferView
		if (!data) {
			const buffer = new ArrayBuffer(byteLength)
			data = bytesPerSample === 2 ? new Uint16Array(buffer) : new Uint8Array(buffer)
		}

		const bytesPerElement = data instanceof Uint16Array ? 2 : 1
		const rElementLength = rByteLength / bytesPerElement
		const gElementLength = gByteLength / bytesPerElement
		const bElementLength = bByteLength / bytesPerElement
		const rOffsetElements = 0
		const gOffsetElements = rOffsetElements + rElementLength
		const bOffsetElements = gOffsetElements + gElementLength
		const rView = data.subarray(rOffsetElements, gOffsetElements)
		const gView = data.subarray(gOffsetElements, bOffsetElements)
		const bView = data.subarray(bOffsetElements, bOffsetElements + bElementLength)

		await Promise.all([
			rgbPlaneOutput.r.readAsync({ packAlignment: 1, bufferView: rView }),
			rgbPlaneOutput.g.readAsync({ packAlignment: 1, bufferView: gView }),
			rgbPlaneOutput.b.readAsync({ packAlignment: 1, bufferView: bView }),
		])

		editor.renderer.scratchBuffers.updateSize(0, 0)

		return {
			data,
			rgbPlanesDataLayout: {
				rOffset: 0,
				rLength: rByteLength,

				gOffset: rByteLength,
				gLength: gByteLength,

				bOffset: rByteLength + gByteLength,
				bLength: bByteLength,
			},
			width,
			height,
			colorSpace,
		}
	} else {
		let bufferView = options.bufferView
		const byteSize = outputTexture.getByteLength()
		const bytesPerPixel = outputTexture.getBytesPerPixel()
		const bytesPerSample = bytesPerPixel >> 2

		if (bufferView && bufferView.byteLength < byteSize) {
			throw new Error("RGBA export buffer is smaller than the required byte length")
		}

		if (
			bufferView &&
			((bytesPerSample === 2 && !(bufferView instanceof Uint16Array)) ||
				(bytesPerSample === 1 && !(bufferView instanceof Uint8Array)))
		) {
			throw new Error("RGBA export buffer type does not match the output texture format")
		}

		if (!bufferView) {
			const buffer = new ArrayBuffer(byteSize)
			bufferView = outputTexture.getBufferView({ buffer }) as
				| Uint8Array
				| Uint16Array
				| undefined
		}

		const data = outputTexture.read({ bufferView })

		return {
			data,
			width,
			height,
			colorSpace,
		}
	}
}

export function exportImage(editor: Editor, options: JpegExportImageOptions): Promise<Blob | null>
export function exportImage(
	editor: Editor,
	options: NonJpegExportImageOptions,
): Promise<Blob | null>
export function exportImage(
	editor: Editor,
	options: AnyImageTypeExportImageOptions,
): Promise<Blob | null>
export async function exportImage(
	editor: Editor,
	options: ExportImageOptions,
): Promise<Blob | null> {
	const imageData = await exportImageData(editor, options)

	if (options.type === "jpg") {
		if (!options.jpegEncoder) {
			throw new Error("Cannot find JPEG encoder")
		}

		return await encodeImageData(imageData, {
			...options,
			type: "jpg",
			jpegEncoder: options.jpegEncoder,
		})
	}

	return await encodeImageData(imageData, {
		...options,
		type: options.type,
	})
}

export function encodeImageData(
	imageData: PixelData,
	options: JpegEncodeImageDataOptions,
): Promise<Blob>
export function encodeImageData(
	imageData: PixelData,
	options: NonJpegEncodeImageDataOptions,
): Promise<Blob>
export function encodeImageData(
	imageData: PixelData,
	options: AnyImageTypeEncodeImageDataOptions,
): Promise<Blob>
export async function encodeImageData(
	imageData: PixelData,
	options: EncodeImageDataOptions | AnyImageTypeEncodeImageDataOptions,
): Promise<Blob> {
	switch (options.type) {
		case "tif":
			return await (options.tiffEncoder?.encodeTIFF(imageData, { ppi: options.ppi }) ??
				encodeTIFF(imageData, { ppi: options.ppi }))
		case "dng":
			return encodeDNG(imageData)
		case "png":
			return await encodePNG(imageData)
		case "jpg":
			return await encodeJpeg(imageData, options)
		case "psd":
			return await (options.psdEncoder?.encodePSD(imageData, { ppi: options.ppi }) ??
				encodePSD(imageData, { ppi: options.ppi }))
	}
}

function resizeDimensions(sourceSize: vec2, options: ResizeOptions) {
	const targetSize: vec2 = [
		options.resizeWidth ?? sourceSize[0],
		options.resizeHeight ?? sourceSize[1],
	]

	if (options.resizeMode === "fit") {
		return normalizeExportSize(fitToSize(sourceSize, targetSize))
	}

	if (options.resizeMode === "fill") {
		return normalizeExportSize(fillToSize(sourceSize, targetSize))
	}

	return normalizeExportSize(targetSize)
}

function normalizeExportSize(size: vec2): vec2 {
	return [Math.round(size[0]), Math.round(size[1])]
}
